import hash from 'object-hash';

import { Notification } from 'Lib/types';

import connectToWebSockets, {
  SocketData,
  SocketHandler,
} from '../../../lib/connectToWebSockets';

const showActivityLogs = process.env.SHOW_LOGS === 'true';
let NOTIFICATION_IDX = 0;
const NOTIFICATIONS: Notification[] = [];
const EVENT_TABLE = new Map();

const publish = (id: string, data: SocketData) => {
  if (EVENT_TABLE.has(id)) {
    const handlerMapper = EVENT_TABLE.get(id);

    for (const key in handlerMapper) {
      if (handlerMapper.hasOwnProperty(key)) {
        const func = handlerMapper[key];
        func(data);
      }
    }
  }
};

const getId = (actionType: string, actionName: string) => {
  if (typeof actionType !== 'string' || typeof actionName !== 'string') {
    console.error(
      `actionType: ${actionType} or actionName: ${actionName} is not string`,
    );
    return '';
  }

  return hash({
    actionType,
    actionName,
  });
};

const getHandlerId = (_handler: SocketHandler) => hash(_handler);

export default class Activities {
  static handler = (data: SocketData) => {
    const { action, actionName } = data;
    if (action && actionName) {
      const id = getId(action, actionName);
      publish(id, data);
    }
  };

  static connect() {
    return connectToWebSockets(this.handler);
  }

  static getCurrentIndex() {
    return NOTIFICATION_IDX;
  }

  /**
   * This method is to attach an handler to EVENT_TABLE
   */
  static attachHandler(id: string, _handler: SocketHandler): boolean {
    if (typeof _handler !== 'function') return false;

    const handlerId = getHandlerId(_handler);

    if (EVENT_TABLE.has(id)) {
      const handlerMapper = EVENT_TABLE.get(id);
      handlerMapper[handlerId] = _handler;
      EVENT_TABLE.set(id, handlerMapper);
    } else {
      EVENT_TABLE.set(id, {
        [handlerId]: _handler,
      });
    }

    return true;
  }

  /**
   * This method is to detach an handler from EVENT_TABLE
   */
  static detachHandler(id: string, handlerId: string): boolean {
    if (EVENT_TABLE.has(id) && EVENT_TABLE.get(id).hasOwnProperty(handlerId)) {
      const handlerMapper = EVENT_TABLE.get(id);
      delete handlerMapper[handlerId];

      if (Object.keys(handlerMapper).length === 0) {
        EVENT_TABLE.delete(id);
      } else {
        EVENT_TABLE.set(id, handlerMapper);
      }

      return true;
    }

    return false;
  }

  /**
   * This method is to subscribe to an event
   */
  static subscribe(
    actionType: string,
    actionName: string,
    _handler: SocketHandler,
  ) {
    const id = getId(actionType, actionName);
    const handlerId = getHandlerId(_handler);

    // Validation if same handler was attached earlier
    if (EVENT_TABLE.has(id)) {
      const handlerMapper = EVENT_TABLE.get(id);

      for (const key in handlerMapper) {
        if (handlerMapper.hasOwnProperty(key)) {
          const func = handlerMapper[key];

          if (
            func.toString() === _handler.toString() ||
            func === _handler ||
            handlerId === key
          ) {
            console.error(
              'You are re-attaching the same function (although maybe in a different context), this may or may not be an issue.',
              actionType,
              actionName,
              _handler,
              func,
            );
            return handlerId;
          }
        }
      }
    }

    Activities.attachHandler(id, _handler);

    if (showActivityLogs) {
      console.info(
        `[Activities.ts] Successfully subscribed to ${actionType}.${actionName} => ${handlerId}`,
        _handler,
      );
    }

    return handlerId;
  }

  /**
   * This method is to unsubscribe event by proviing the same handler
   */
  static unsubscribe(
    actionType: string,
    actionName: string,
    _handler: SocketHandler,
  ): boolean {
    const handlerId = getHandlerId(_handler);
    return Activities.unsubscribeById(actionType, actionName, handlerId);
  }

  /**
   * This method is to unsubscribe event by providing subscribed id
   */
  static unsubscribeById(
    actionType: string,
    actionName: string,
    handlerId: string,
  ): boolean {
    const id = getId(actionType, actionName);

    const isSuccess = Activities.detachHandler(id, handlerId);

    if (isSuccess) {
      if (showActivityLogs) {
        console.info(
          `[Activities.ts] Successfully unsubscribed ${handlerId} from ${actionType}.${actionName}`,
        );
      }
    } else {
      console.warn(
        `[Activities.ts] Failed to unsubscribe ${handlerId} from ${actionType}.${actionName}`,
      );
    }

    return isSuccess;
  }

  static push(notification: Notification) {
    const idx = (NOTIFICATION_IDX += 1);

    NOTIFICATIONS.push({ ...notification, idx });
    return idx;
  }

  static update(notification: Notification) {
    const idx = this.indexOf(notification.id);
    if (idx !== -1) {
      NOTIFICATIONS.splice(idx, 1, { ...notification, idx });
      return true;
    }

    return false;
  }

  static upsert(notification: Notification) {
    // if id exists already
    if (this.indexOf(notification.id) !== -1) {
      this.update({ ...notification, isDismissibleDuringProcessing: false });
    } else {
      this.push({ ...notification, isDismissibleDuringProcessing: false });
    }
  }

  static get notifications() {
    return NOTIFICATIONS;
  }

  static remove(id: string) {
    const index = this.indexOf(id);

    if (index !== -1) {
      NOTIFICATIONS.splice(index, 1);
      return true;
    }

    return false;
  }

  static indexOf(id: string) {
    return NOTIFICATIONS.findIndex((x) => x.id === id);
  }

  static has(id: string) {
    return this.indexOf(id) !== -1;
  }
}
